import type { ReactNode } from 'react';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * One state of the connect dialog: its title, the body, and a footer of
 * actions under a hairline. Each state mounts fresh, so a short fade carries
 * the change from one to the next.
 */
export function ConnectFrame({
  title,
  label,
  children,
  footer,
}: {
  /** The title in the header; omit when the body leads with its own. */
  title?: ReactNode;
  /** What the dialog is, for a screen reader, when the title is not it. */
  label: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className='animate-in duration-150 fade-in-0 motion-reduce:animate-none'>
      <DialogHeader className={title ? 'px-6 pt-6 pr-12' : 'sr-only'}>
        <DialogTitle className='text-base leading-6 font-semibold'>
          {title ?? label}
        </DialogTitle>
        <DialogDescription className='sr-only'>{label}</DialogDescription>
      </DialogHeader>
      <div className={cn('px-6 pb-5', title ? 'pt-4' : 'pt-6')}>{children}</div>
      {footer && (
        <DialogFooter className='items-center gap-2 border-t border-border/60 px-6 py-3 sm:space-x-0'>
          {footer}
        </DialogFooter>
      )}
    </div>
  );
}

/** A card of rows, hairlines between: the dialog's one grouping shape. */
export function ConnectCard({
  heading,
  children,
  className,
}: {
  heading?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-border',
        className
      )}
    >
      {heading && (
        <p className='px-3.5 pt-3 pb-1 text-xs font-medium text-muted-foreground'>
          {heading}
        </p>
      )}
      <div className='divide-y divide-border/60'>{children}</div>
    </div>
  );
}
